import classNames from 'classnames/bind';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import React from 'react';

import {
  IMAGE_SHOW_DURATION,
  ANIMATION_SPEED_FACTOR,
  LOGO_SHOW_DURATION,
  SHOW_DELAY,
  HIDE_DURATION,
  IMAGE_DELAY,
  SHOW_EASE,
  IMAGE_SHOW_EASE,
  HIDE_EASE,
} from 'constants/hero-animation-variables';
import AdidasLogoLarge from 'images/pages/home/hero/adidas-large.inline.svg';
import AdidasLogo from 'images/pages/home/hero/adidas.inline.svg';

import imageStyles from '../image-styles.module.scss';
import Trademark from '../trademark';

import styles from './slide-adidas.module.scss';

const cx = classNames.bind(styles);
const imgCx = classNames.bind(imageStyles);

const SlideAdidas = () => {
  const {
    adidasTop,
    adidasBottom,
    adidasLeft,
    adidasRight,
    // eslint-disable-next-line no-use-before-define
  } = useStaticQuery(IMAGE_QUERY);

  const trademarkVariants = {
    initial: {
      y: '100vh',
    },
    appear: {
      y: '100vh',
    },
    showAdidas: {
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SHOW_DELAY,
        duration: ANIMATION_SPEED_FACTOR * LOGO_SHOW_DURATION,
        ease: SHOW_EASE,
      },
    },
    hideAdidas: {
      y: '-100vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    },
  };

  const adidasLogoVariants = {
    initial: {
      y: '100vh',
    },
    showAdidas: {
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SHOW_DELAY,
        duration: ANIMATION_SPEED_FACTOR * LOGO_SHOW_DURATION,
        ease: SHOW_EASE,
      },
    },
    hideAdidas: {
      y: '-120vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    },
  };

  const imageVariants = {
    initial: {
      y: '100vh',
    },
    appear: {
      y: '100vh',
    },
    showAdidas: (custom) => ({
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * (IMAGE_DELAY[custom].show + SHOW_DELAY),
        duration: ANIMATION_SPEED_FACTOR * IMAGE_SHOW_DURATION,
        ease: IMAGE_SHOW_EASE,
      },
    }),
    hideAdidas: (custom) => ({
      y: '-100vh',
      transition: {
        delay: ANIMATION_SPEED_FACTOR * IMAGE_DELAY[custom].hide,
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    }),
  };

  const adidasSmallVariants = {
    initial: {
      y: '100vh',
    },
    showAdidas: {
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SHOW_DELAY,
        duration: ANIMATION_SPEED_FACTOR * LOGO_SHOW_DURATION,
        ease: SHOW_EASE,
      },
    },
    hideAdidas: {
      y: '-100vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    },
  };

  return (
    <>
      <motion.div variants={imageVariants} className={imgCx('img-left')} custom="left">
        <GatsbyImage fluid={adidasLeft.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-bottom')} custom="bottom">
        <GatsbyImage fluid={adidasBottom.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-top')} custom="top">
        <GatsbyImage fluid={adidasTop.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-right')} custom="right">
        <GatsbyImage fluid={adidasRight.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div className={cx('trademark')} variants={trademarkVariants}>
        <Trademark />
      </motion.div>
      <motion.div className={cx('adidas-small')} variants={adidasSmallVariants}>
        <AdidasLogo />
      </motion.div>
      <motion.div className={cx('adidas-logo')} variants={adidasLogoVariants}>
        <AdidasLogoLarge />
      </motion.div>
    </>
  );
};

export default SlideAdidas;

const IMAGE_QUERY = graphql`
  query {
    adidasLeft: file(relativePath: { eq: "pages/home/hero/adidas-left.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    adidasRight: file(relativePath: { eq: "pages/home/hero/adidas-right.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    adidasTop: file(relativePath: { eq: "pages/home/hero/adidas-top.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    adidasBottom: file(relativePath: { eq: "pages/home/hero/adidas-bottom.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`;
